import React, { useCallback, useEffect, useRef } from "react";

import { useArticleInfoQuery } from "@api/articleInfo/useArticleInfo";
import { KEY_CLINICAL_TRIALS } from "@FillInTableContainer/components/FillInTable/constants/clinicalTrials.constants";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { ENABLE_GROUP_TABLE_KEY_LIST } from "@FillInTableContainer/shared/constants/tableCreatorGroupTable.constants";
import {
  DEFAULT_FOCUS_STATUS,
  FocusStatus,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import {
  DEFAULT_POSITION_INFO,
  PositionInfo,
  useTableCreatorPositionInfo,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useGetNumberOfGroupShell } from "@FillInTableContainer/shared/hooks/useGetNumberOfGroupShell";
import { isTableCustomTitle } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getTableCustomTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { useHandleCompanyWebsiteVariousPagesDrugProfileTable } from "./useHandleCompanyWebsiteVariousPagesDrugProfileTable";
import { TableInfoProps } from "../CompanyWebsiteVariousPagesTableCreatorTable";

interface UseHandleTableCreatorTableReturn {
  info: PositionInfo;
  focusStatus: FocusStatus;
  isFocused: boolean;
  isLastTable: boolean;
  isVisibleTableCustomTitle: boolean;
  tableWrapperRef: React.RefObject<HTMLDivElement>;
  tableCustomTitle: string;
  isEnableEdit: boolean;
  isFocusedAddButton: boolean;
  focusEventToChangePositionInfo: (focusEnterType: FocusStatus) => void;
  numberOfGroupShellCount: (groupShellKey: string | null) => number;
  handleClickDeleteButton: () => void;
  changePositionInfo: () => void;
  changePositionInfoToDefault: () => void;
  changeFocusStatus: (focusStatus: FocusStatus) => void;
  handleFocusGroupShell: (
    groupShellKey: string | null,
    groupShellIndex: number,
  ) => void;
  handleDefaultFocus: () => void;
}

export const useHandleCompanyWebsiteVariousPagesTableCreatorTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const {
    groupTableKey,
    tableKey,
    groupTableIndex,
    tableIndex,
    tableCount,
    groupShellList,
  } = tablePropsInfo;

  const articleSection = useArticleSection();

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const [entryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { data: articleInfo } = useArticleInfoQuery(
    Number(getPressIdFromURL()),
  );

  const {
    handleClickDrugProfileDeleteButton,
    handleKeyDownToDeleteDrugProfileTable,
  } = useHandleCompanyWebsiteVariousPagesDrugProfileTable(tablePropsInfo);

  const isEnableEdit = articleInfo
    ? ENABLE_GROUP_TABLE_KEY_LIST[articleSection][groupTableKey].includes(
        articleInfo.draftArticleStatus,
      )
    : false;

  const { groupShellCount } = useGetNumberOfGroupShell();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const isLastTable = tableIndex === tableCount - 1;

  const isFocused =
    info.groupTableKey === groupTableKey &&
    info.tableKey === tableKey &&
    info.tableIndex === tableIndex &&
    info.groupShellKey === null &&
    info.groupShellIndex === null &&
    info.shellKey === null &&
    info.shellIndex === null &&
    info.valueIndex === null &&
    isEnableEdit;

  const isFocusedAddButton =
    focusStatus.container === "noFocus" && isEnableEdit;

  const tableCustomTitle = getTableCustomTitle(
    articleSection,
    entryData,
    groupTableKey,
    tableIndex,
  );
  const isVisibleTableCustomTitle =
    isTableCustomTitle(articleSection, groupTableKey) &&
    groupShellCount(groupShellList[0].groupShellKey, tableIndex) > 1;

  const numberOfGroupShellCount = (groupShellKey: string | null) =>
    groupShellCount(groupShellKey, tableIndex);

  const changePositionInfo = () => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey: null,
      groupShellIndex: null,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
  };

  const handleFocusGroupShell = (
    groupShellKey: string | null,
    groupShellIndex: number,
  ) => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      groupShellKey,
      groupShellIndex,
      tableIndex,
      tableKey,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
    changeFocusStatus({ type: "clear", container: "groupShell" });
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const changePositionInfoToDefault = () => {
    changeInfo(DEFAULT_POSITION_INFO);
  };

  const handleDefaultFocus = () => {
    changeInfo(DEFAULT_POSITION_INFO);
    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const focusEventToChangePositionInfo = (focusEnterType: FocusStatus) => {
    changeInfo({
      groupTableKey,
      groupTableIndex,
      tableKey,
      tableIndex,
      groupShellKey: null,
      groupShellIndex: null,
      subGroupShellIndex: null,
      subGroupShellKey: null,
      shellKey: null,
      shellIndex: null,
      valueIndex: null,
    });
    changeFocusStatus({ type: "clear", container: "table" });

    if (focusEnterType.type === "clear") {
      changeFocusStatus({ type: "clear", container: "table" });
    }
    changeValidatedInfo(DEFAULT_VALIDATED_INFO);
  };

  const handleClickDeleteButton = () => {
    if (info.groupTableKey === KEY_CLINICAL_TRIALS.drugProfile)
      handleClickDrugProfileDeleteButton();
  };

  const handleKeyDownToDeleteTable = useCallback(
    (event: KeyboardEvent) => {
      if (info.groupTableKey === KEY_CLINICAL_TRIALS.drugProfile)
        handleKeyDownToDeleteDrugProfileTable(event);
    },
    [info.groupTableKey, handleKeyDownToDeleteDrugProfileTable],
  );

  useEffect(() => {
    if (focusStatus.container !== "table") return;
    window.addEventListener("keydown", handleKeyDownToDeleteTable);

    return () => {
      window.removeEventListener("keydown", handleKeyDownToDeleteTable);
    };
  }, [focusStatus, handleKeyDownToDeleteTable]);

  return {
    tableWrapperRef,
    info,
    focusStatus,
    tableCustomTitle,
    isFocused,
    isLastTable,
    isVisibleTableCustomTitle,
    isEnableEdit,
    isFocusedAddButton,
    handleDefaultFocus,
    numberOfGroupShellCount,
    focusEventToChangePositionInfo,
    handleClickDeleteButton,
    changePositionInfo,
    changeFocusStatus,
    changePositionInfoToDefault,
    handleFocusGroupShell,
  };
};
